import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { alertOptions } from 'utils/Config';
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from 'utils/Handlers';

/**
 * Handles error messages and displays a toast if requested.
 * Replicates the error handling logic from CommonServices.ts
 */
const handleError = (error: any, toastShow?: boolean) => {
  let errorMessage = '';

  // Extract error message from various possible response structures
  if (error?.status === 400 || error?.error?.status === 400) {
    if (Array.isArray(error?.error?.data?.message)) {
      if (typeof error?.error?.data?.message?.[0] === 'string') {
        errorMessage = error?.error?.data?.message?.[0];
      }
    } else {
      errorMessage =
        error?.message ||
        error?.error?.message ||
        error?.error?.data?.message ||
        '';
    }
  } else {
    if (typeof error?.error?.message === 'string') {
      errorMessage = error?.error?.message;
    } else {
      errorMessage = error?.message || error?.error?.message || '';
    }
  }

  if (toastShow && errorMessage) {
    toast.error(errorMessage || String(error), alertOptions);
  }

  throw errorMessage || error;
};

/**
 * Hook for fetching list data.
 * Replaces getAllListData from CommonServices.ts
 */
export const useFetchList = <T = any>(
  queryKey: any[],
  endpoint: string,
  options: any = {},
) => {
  const { toastShow = false, ...queryOptions } = options;

  return useQuery<T>({
    queryFn: async () => {
      try {
        const response: any = await getRequest({ url: endpoint });
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
    queryKey,
    ...queryOptions,
  });
};

/**
 * Hook for fetching list data with infinite scrolling.
 */
export const useFetchInfiniteList = <T = any>(
  queryKey: any[],
  endpoint: string,
  options: any = {},
) => {
  const { toastShow = false, take = 20, ...queryOptions } = options;

  return useInfiniteQuery<T>({
    getNextPageParam: (lastPage: any, allPages: any) => {
      const currentCount = allPages.length * take;
      return lastPage.total > currentCount ? currentCount : undefined;
    },
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }: any) => {
      try {
        const separator = endpoint.includes('?') ? '&' : '?';
        const url = `${String(endpoint)}${String(separator)}skip=${String(
          pageParam,
        )}&take=${String(take)}`;
        const response: any = await getRequest({ url });
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
    queryKey,
    ...queryOptions,
  });
};

/**
 * Hook for fetching single record data.
 * Replaces getSingleData from CommonServices.ts
 */
export const useFetchSingleData = <T = any>(
  queryKey: any[],
  id: string,
  endpoint: string,
  options: any = {},
) => {
  const { toastShow = false, ...queryOptions } = options;

  return useQuery<T>({
    queryFn: async () => {
      try {
        const url = id !== '' ? `${endpoint}/?id=${id}` : endpoint;
        const response: any = await getRequest({ url });
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
    queryKey,
    ...queryOptions,
  });
};

/**
 * Hook for creating new data.
 * Replaces addNewData from CommonServices.ts
 */
export const useCreateItem = (toastShow = true) => {
  return useMutation({
    mutationFn: async ({
      data,
      endpoint,
      options,
    }: {
      data: any;
      endpoint: string;
      options?: any;
    }) => {
      try {
        const response: any = await postRequest(
          { data, url: endpoint },
          options,
        );
        if (toastShow && response?.message) {
          toast.success(response.message, alertOptions);
        }
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
  });
};

/**
 * Hook for updating data.
 * Replaces updateData from CommonServices.ts
 */
export const useUpdateItem = (toastShow = true) => {
  return useMutation({
    mutationFn: async ({
      data,
      endpoint,
      id = '',
    }: {
      data: any;
      endpoint: string;
      id?: string;
    }) => {
      try {
        const url = id !== '' ? `${endpoint}/${id}` : endpoint;
        const response: any = await putRequest({ data, url });
        if (toastShow && response?.message) {
          toast.success(response.message, alertOptions);
        }
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
  });
};

/**
 * Hook for patching/replacing data.
 * Replaces ReplaceData from CommonServices.ts
 */
export const usePatchItem = (toastShow = true) => {
  return useMutation({
    mutationFn: async ({
      data,
      endpoint,
      id = '',
    }: {
      data: any;
      endpoint: string;
      id?: string;
    }) => {
      try {
        const url = id !== '' ? `${endpoint}/${id}` : endpoint;
        const response: any = await patchRequest({ data, url });
        if (toastShow && response?.message) {
          toast.success(response.message, alertOptions);
        }
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
  });
};

/**
 * Hook for deleting data.
 * Replaces deleteData from CommonServices.ts
 */
export const useDeleteItem = (toastShow = true) => {
  return useMutation({
    mutationFn: async ({ endpoint, id }: { endpoint: string; id?: string }) => {
      try {
        const url = id ? `${endpoint}/${id}` : endpoint;
        const response: any = await deleteRequest({ url });
        if (toastShow && response?.message) {
          toast.success(response.message, alertOptions);
        }
        return response;
      } catch (error) {
        return handleError(error, toastShow);
      }
    },
  });
};
